import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { RelayEnvironmentProvider } from 'react-relay/hooks';
import RelayEnvironment from 'lib/utils/relay';
import * as serviceWorker from 'serviceWorker';
import 'sanitize.css/sanitize.css';
import 'styles/__fonts.css';
import { ThemeProvider } from 'styled-components';
import { LoaderDots } from 'app/components/LoaderDots';
import { Toast } from '@unmind/design-system-components-web';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import StyledTheme from 'styles/theme';

import 'antd/dist/antd.css';

import App from 'app';

import { HelmetProvider } from 'react-helmet-async';

import './locales/i18n';
import { LDProvider } from 'lib/LaunchDarkly/LDProvider';
import { DatadogSetup } from 'lib/Datadog/DatadogSetup';
import { isDev } from 'lib/utils/isDev';

const MOUNT_NODE = document.getElementById('root') as HTMLElement;

interface Props {
  Component: typeof App;
}

const customConf = {
  mediaQuery: 'only screen',
  columns: {
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12,
  },
  breakpoints: {
    xs: 1,
    sm: 48, // 768px
    md: 64, // 1024px
    lg: 90, // 1440px
    xl: 120, // 1920px
  },
  gutterWidth: {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 3,
  },
  paddingWidth: {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 3,
  },
  container: {
    xs: 'full', // 'full' = max-width: 100%
    sm: 'full', // 'full' = max-width: 100%
    md: 'full', // 'full' = max-width: 100%
    lg: 84, // max-width: 1440px
    xl: 84, // max-width: 1440px
  },
};

if (!isDev()) {
  const tracingOrigins: string[] = [
    (process.env.REACT_APP_API_BASE_URL ?? '').replace(/https{0,1}:\/\//, ''),
    (process.env.REACT_APP_API_GATEWAY_BASE_URL ?? '').replace(/https{0,1}:\/\//, ''),
  ].filter((origin: string): boolean => !!origin);
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_KEY,
    integrations: [
      new Integrations.BrowserTracing({
        tracingOrigins,
      }),
    ],
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_SAMPLE_RATE),
    environment: process.env.REACT_APP_BUILD_ENV,
  });
}

const ConnectedApp = ({ Component }: Props) => (
  <LDProvider>
    <ThemeProvider theme={{ awesomegrid: customConf }}>
      <ThemeProvider theme={StyledTheme}>
        <RelayEnvironmentProvider environment={RelayEnvironment}>
          <React.Suspense fallback={<LoaderDots />}>
            <HelmetProvider>
              <DatadogSetup />
              <Component />
              <Toast />
            </HelmetProvider>
          </React.Suspense>
        </RelayEnvironmentProvider>
      </ThemeProvider>
    </ThemeProvider>
  </LDProvider>
);

const render = (Component: typeof App) => {
  return ReactDOM.render(<ConnectedApp Component={Component} />, MOUNT_NODE);
};

if (module.hot) {
  module.hot.accept(['./app', './locales/i18n'], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    const App = require('./app').App;
    render(App);
  });
}

render(App);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
